import { useCallback, useEffect, useRef, useState } from "react";
import { GetUsersRequestsAll, UserAditional } from "@services/getUsers";
import { MODALS_ENUM } from "@constants/Modals";
import { useDispatch, useSelector } from "react-redux";
import { show } from "redux-modal";
import { useNavigate } from "react-router-dom";
import { ROUTES_E } from "@utils/RoutsName";
import { getSorting, saveSorting } from "@utils/localeSorting";
import AUTHSTATES from "@store/auth/AuthStates";
import { RootState } from "@store";
import useApiRequests from "@services/http/useApiRequest";
import { RecordsResponse } from "@services/http/responses/BaseResponse";
import { useAllContractsUpdater } from "@utils/hooks/useCurrentUser";
import { CLIENT_SEARCH_KEY } from "./ClientSearch/useClientSearchControl";
import { useIntl } from "react-intl";
import { UserRole } from "@services/http/responses/AccountResponse";
import { CLIENT_ROW_ACCESS, CLIENT_TABLE_COLUMNS, SortUsersE, useClientsTableUtils } from "./Clients.utils";
import User from "../User/User";

export enum BtnStateE {
  HASH,
  TIME,
}

const PAGE_SIZE = 100;

export const useClientsControl = () => {

  const auth = useSelector((state: RootState) => state.auth); // useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const requests = useApiRequests();
  const [loading, setLoading] = useState<boolean>(true);
  const [sortLoading, setSortLoading] = useState(false);
  const [users, setUsers] = useState<UserAditional[]>([]);
  //сортировка
  const [sort, setSort] = useState<SortUsersE | undefined>(
    getSorting("sort_clients")
  );
  const firstRender = useRef(true);
  const getAllContracts = useAllContractsUpdater();
  const { formatMessage } = useIntl();
  const utils = useClientsTableUtils();

  async function getUsers(
    offset: number = 0,
    limit: number = PAGE_SIZE,
    search?: string
  ): Promise<RecordsResponse<UserAditional> | void> {
    const searchParam =
      search || localStorage.getItem(CLIENT_SEARCH_KEY) || undefined;
    return await requests.enqueue<RecordsResponse<UserAditional>>(
      new GetUsersRequestsAll(offset, limit, searchParam)
    );
  }

  function sortById(
    arr: UserAditional[],
    pagination: boolean = false
  ): UserAditional[] {
    if (!firstRender.current && sort === SortUsersE.ID) {
      setSort(undefined);
      return arr.sort((a, b) =>
        a.id.localeCompare(b.id, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
    }
    if (firstRender.current || !pagination) {
      firstRender.current = false;
      return arr.sort((a, b) =>
        b.id.localeCompare(a.id, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
    }
    return arr.sort((a, b) =>
      a.id.localeCompare(b.id, undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );
  }

  function sortByEmail(
    arr: UserAditional[],
    pagination: boolean = false
  ): UserAditional[] {
    if (!firstRender.current && sort === SortUsersE.EMAIL) {
      setSort(undefined);
      return arr.sort((a, b) => a.email.localeCompare(b.email));
    }
    if (firstRender.current || !pagination) {
      firstRender.current = false;
      return arr.sort((a, b) => b.email.localeCompare(a.email));
    }
    return arr.sort((a, b) => a.email.localeCompare(b.email));
  }

  function sortByRole(
    arr: UserAditional[],
    pagination: boolean = false
  ): UserAditional[] {
    if (!firstRender.current && sort === SortUsersE.ROLE) {
      setSort(undefined);
      return arr.sort((a, b) => Number(a.Urole?.localeCompare(b.Urole || "")));
    }
    if (firstRender.current || !pagination) {
      firstRender.current = false;
      return arr.sort((a, b) => Number(b.Urole?.localeCompare(a.email || "")));
    }
    return arr.sort((a, b) => Number(a.Urole?.localeCompare(b.email || "")));
  }

  function sortByName(
    arr: UserAditional[],
    pagination: boolean = false
  ): UserAditional[] {
    if (!firstRender.current && sort === SortUsersE.NAME) {
      setSort(undefined);
      return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    if (firstRender.current || !pagination) {
      firstRender.current = false;
      return arr.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    }
    return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }

  function sortByFa(
    arr: UserAditional[],
    pagination: boolean = false
  ): UserAditional[] {
    if (!firstRender.current && sort === SortUsersE.FA) {
      setSort(undefined);
      return arr.sort((a, b) => Number(b["2fa"]) - Number(a["2fa"]));
    }
    if (firstRender.current || !pagination) {
      firstRender.current = false;
      return arr.sort((a, b) => Number(a["2fa"]) - Number(b["2fa"]));
    }
    return arr.sort((a, b) => Number(b["2fa"]) - Number(a["2fa"]));
  }

  function sortByOnline(
    arr: UserAditional[],
    pagination: boolean = false
  ): UserAditional[] {
    if (!firstRender.current && sort === SortUsersE.ONLINE) {
      setSort(undefined);
      return arr.sort((a, b) => b.m_online - a.m_online);
    }
    if (firstRender.current || !pagination) {
      firstRender.current = false;
      return arr.sort((a, b) => a.m_online - b.m_online);
    }
    return arr.sort((a, b) => b.m_online - a.m_online);
  }

  function sortByLow(
    arr: UserAditional[],
    pagination: boolean = false
  ): UserAditional[] {
    if (!firstRender.current && sort === SortUsersE.LOW) {
      setSort(undefined);
      return arr.sort((a, b) => b.m_low - a.m_low);
    }
    if (firstRender.current || !pagination) {
      firstRender.current = false;
      return arr.sort((a, b) => a.m_low - b.m_low);
    }
    return arr.sort((a, b) => b.m_low - a.m_low);
  }

  function sortByOffline(
    arr: UserAditional[],
    pagination: boolean = false
  ): UserAditional[] {
    if (!firstRender.current && sort === SortUsersE.OFFLINE) {
      setSort(undefined);
      return arr.sort((a, b) => b.m_offline - a.m_offline);
    }
    if (firstRender.current || !pagination) {
      firstRender.current = false;
      return arr.sort((a, b) => a.m_offline - b.m_offline);
    }
    return arr.sort((a, b) => b.m_offline - a.m_offline);
  }

  const sortUsers = (s?: SortUsersE, arr = users, pagination = false) => {
    setSort(s);
    setSortLoading(true);
    let sorted: UserAditional[] = [];
    switch (s) {
      case SortUsersE.ID:
        sorted = sortById(arr, pagination);
        break;
      case SortUsersE.EMAIL:
        sorted = sortByEmail(arr, pagination);
        break;
      case SortUsersE.ROLE:
        sorted = sortByRole(arr, pagination);
        break;
      case SortUsersE.NAME:
        sorted = sortByName(arr, pagination);
        break;
      case SortUsersE.FA:
        sorted = sortByFa(arr, pagination);
        break;
      case SortUsersE.ONLINE:
        sorted = sortByOnline(arr, pagination);
        break;
      case SortUsersE.LOW:
        sorted = sortByLow(arr, pagination);
        break;
      case SortUsersE.OFFLINE:
        sorted = sortByOffline(arr, pagination);
        break;
      default:
        sorted = arr;
    }
    setSortLoading(false);
    setUsers(sorted);
  };

  //refresh
  const [refresh, setRefresh] = useState<number>(0);

  const onFilterUsers = (id: string) => {
    setUsers(users.filter((u) => u.id !== id));
  };

  useEffect(() => {
    if (auth.state === AUTHSTATES.UNAUTHORIZED) {
      return navigate(ROUTES_E.HOME);
    }
    // IS_DEBUG && setUsers(mockUsers);
  }, [auth.state]);

  const onSearch = async (value: string) => {
    const res = await getUsers(0, PAGE_SIZE, value);
    if (!res) return;

    setUsers(res.Records);
  };

  const onUpdateUser = (client: UserAditional) => {
    const index = users.findIndex((us) => us.id === client.id);

    if (index === undefined) return;

    setUsers([...users.slice(0, index), client, ...users.slice(index + 1)]);
  };

  useEffect(() => {
    getAllContracts();
  }, []);

  const onRefreshClients = () => setRefresh((prev) => prev + 1);

  function addClient() {
    dispatch(
      show(MODALS_ENUM.ADD_NEW_CLIENT, {
        title: formatMessage({ id: "modal.new_client.title" }),
        onRefresh: () => setRefresh((prev) => prev + 1),
      })
    );
  }

  function addTech() {
    dispatch(
      show(MODALS_ENUM.ADD_NEW_CLIENT, {
        title: formatMessage({ id: "clients.create_tech_user" }),
        singleRole: UserRole.Tech,
        onRefresh: () => setRefresh((prev) => prev + 1),
      })
    );
  }

  const saveSort = useCallback(() => {
    saveSorting("sort_clients", sort);
  }, [sort]);


  const [allowCreateTech, setAllowCreateTech] = useState(false);

  useEffect(() => {
    setAllowCreateTech(auth.role === UserRole.Admin);
  }, [auth.role]);

  const filterTableHead = useCallback(() => {
    return CLIENT_TABLE_COLUMNS
      .filter((item) => CLIENT_ROW_ACCESS[item.id as keyof typeof CLIENT_ROW_ACCESS]?.includes(auth.role));
  }, [auth.role]);

  const [roleClassName, setRoleClassName] = useState<string>("");

  useEffect(() => {
    setRoleClassName(utils.getRoleClassName(auth.role));
  }, [auth.role, utils]);

  return {
    pageSize: PAGE_SIZE,
    sortLoading,
    users,
    sort,
    refresh,
    isLoading: requests.isLoading,
    roleClassName,
    allowCreateTech,
    filterTableHead,
    saveSort,
    getUsers,
    setLoading,
    sortUsers,
    onSearch,
    onFilterUsers,
    onUpdateUser,
    onRefreshClients,
    addClient,
    addTech,
  };
};
